
import { defineComponent } from 'vue'
import { AxiosResponse } from 'axios'
import registryDescriptor from './RegistryDescriptor.json'
import RegistryDatatable from './tables/RegistryDatatable.vue'
import RegistryPivotDatatable from './tables/RegistryPivotDatatable.vue'
import RegistryFiltersCard from './RegistryFiltersCard.vue'
import { formatDate } from '@/helpers/commons/localeHelper'

export default defineComponent({
    name: 'registry',
    components: {
        RegistryDatatable,
        RegistryPivotDatatable,
        RegistryFiltersCard
    },
    props: { id: { type: String }, reloadTrigger: { type: Boolean } },
    data() {
        return {
            registryDescriptor,
            registry: {} as any,
            configuration: [] as any[],
            columns: [] as any[],
            rows: [] as any[],
            columnMap: {} as any,
            pagination: { start: 0, limit: 15 } as any,
            updatedRows: [] as any,
            filters: [] as any[],
            selectedFilters: [] as any[],
            entity: null as string | null,
            stopWarningsState: [] as any[],
            isPivot: false,
            loading: false
        }
    },
    watch: {
        async id() {
            await this.loadPage()
            this.stopWarningsState = []
        },
        async reloadTrigger() {
            await this.loadPage()
            this.stopWarningsState = []
        }
    },
    async created() {
        await this.loadPage()
    },
    methods: {
        async loadPage() {
            this.loading = true
            await this.loadRegistry()
            this.loadRegistryData()
            this.loading = false
        },
        async loadRegistry() {
            const postData = new URLSearchParams()

            if (this.pagination.size > registryDescriptor.paginationLimit) {
                postData.append('limit', '' + registryDescriptor.paginationNumberOfItems)
            }

            this.selectedFilters.forEach((el: any) => {
                if (el.filterValue) {
                    postData.append(el.field, el.filterValue)
                }
            })

            postData.append('start', '' + this.pagination.start)
            await this.$http
                .post(`/knowageqbeengine/servlet/AdapterHTTP?ACTION_NAME=LOAD_REGISTRY_ACTION&SBI_EXECUTION_ID=${this.id}`, postData, {
                    headers: {
                        Accept: 'application/json, text/plain, */*',
                        'Content-Type': 'application/x-www-form-urlencoded'
                    }
                })
                .then((response: AxiosResponse<any>) => {
                    this.pagination.size = response.data.results
                    this.registry = response.data
                })
                .catch(() => {})
        },
        loadRegistryData() {
            if (this.registry) {
                this.loadConfiguration()
                this.loadEntity()
                this.loadColumns()
                this.loadColumnMap()
                this.loadColumnsInfo()
                this.loadRows()
                this.getFilters()
            }
        },
        loadColumns() {
            this.columns = []
            this.registry.registryConfig.columns.map((el: any) => {
                if (el.type === 'merge') {
                    this.isPivot = true
                }
                this.columns.push(el)
            })
        },
        loadColumnMap() {
            this.columnMap = { id: 'id' }
            for (let i = 1; i < this.registry.metaData.fields.length; i++) {
                this.columnMap[this.registry.metaData.fields[i].name] = this.registry.metaData.fields[i].header
            }
        },
        loadColumnsInfo() {
            for (let i = 1; i < this.registry.metaData.fields.length; i++) {
                this.columns[i - 1].columnInfo = this.registry.metaData.fields[i]
            }
        },
        loadRows() {
            this.rows = []
            const limit = this.pagination.size <= registryDescriptor.paginationLimit ? this.registry.rows.length : registryDescriptor.paginationNumberOfItems
            for (let i = 0; i < limit; i++) {
                const tempRow = {}
                Object.keys(this.registry.rows[i]).forEach((key) => {
                    tempRow[this.columnMap[key]] = this.registry.rows[i][key]
                })
                this.rows.push(tempRow)
            }
        },
        loadConfiguration() {
            this.configuration = this.registry.registryConfig.configurations
        },
        loadEntity() {
            this.entity = this.registry.registryConfig.entity
        },
        onRowChanged(row: any) {
            const tempRow = { ...row }
            const index = this.updatedRows.findIndex((el: any) => el.id === tempRow.id)
            index === -1 ? this.updatedRows.push(tempRow) : (this.updatedRows[index] = tempRow)
        },
        async saveRegistry() {
            this.updatedRows.forEach((el: any) => {
                if (this.isPivot) {
                    this.formatPivotRows(el)
                }

                delete el.id
                delete el.isNew
                delete el.edited
            })

            const updatedRowsToIsoStrings = JSON.parse(JSON.stringify(this.updatedRows))
            updatedRowsToIsoStrings.forEach((el: any) => {
                this.registry.metaData.fields.forEach((element) => {
                    if (el[element.header]) {
                        if (element.type === 'date') {
                            let date = new Date(formatDate(el[element.header], 'toISOString'))
                            let offset = new Date().getTimezoneOffset()

                            el[element.header] = new Date(date.getTime() - offset * 60000)
                        } else if (element.type === 'timestamp') {
                            el[element.header] = formatDate(el[element.header], 'toISOString')
                        }
                    }
                })
            })

            const postData = new URLSearchParams()
            postData.append('records', '' + JSON.stringify(updatedRowsToIsoStrings))
            await this.$http
                .post(`/knowageqbeengine/servlet/AdapterHTTP?ACTION_NAME=UPDATE_RECORDS_ACTION&SBI_EXECUTION_ID=${this.id}`, postData, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } })
                .then(() => {
                    this.$store.commit('setInfo', {
                        title: this.$t('common.toast.updateTitle'),
                        msg: this.$t('common.toast.updateSuccess')
                    })
                    this.pagination.start = 0
                    this.loadPage()
                })
                .finally(() => (this.updatedRows = []))
        },
        async onRowDeleted(row: any) {
            if (this.isPivot) {
                this.formatPivotRows(row)
            }
            const postData = new URLSearchParams()
            postData.append('records', '' + JSON.stringify([row]))
            await this.$http
                .post(`/knowageqbeengine/servlet/AdapterHTTP?ACTION_NAME=DELETE_RECORDS_ACTION&SBI_EXECUTION_ID=${this.id}`, postData, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } })
                .then((response: AxiosResponse<any>) => {
                    this.$store.commit('setInfo', {
                        title: this.$t('common.toast.deleteTitle'),
                        msg: this.$t('common.toast.deleteSuccess')
                    })

                    if (response.data.ids[0]) {
                        const index = this.rows.findIndex((el: any) => el.id === row.id)
                        this.rows.splice(index, 1)
                        this.pagination.size--
                    }
                })
                .catch((response: AxiosResponse<any>) => {
                    this.$store.commit('setError', {
                        title: this.$t('common.error.generic'),
                        msg: response
                    })
                })
        },
        getFilters() {
            this.filters = []
            const tempFilters = this.registry.registryConfig.filters

            for (let i = 0; i < tempFilters.length; i++) {
                const filter = tempFilters[i]

                for (let j = 0; j < this.columns.length; j++) {
                    const column = this.columns[j]
                    if (filter.presentation !== 'DRIVER' && filter.field === column.field) {
                        this.filters.push({
                            title: filter.title,
                            field: filter.field,
                            presentation: filter.presentationType,
                            static: filter.isStatic,
                            visible: filter.isVisible,
                            column: column
                        })
                        break
                    }
                }
            }
        },
        async filterRegistry(filters: any[]) {
            this.selectedFilters = [...filters]
            this.pagination.start = 0
            this.pagination.size = 0
            await this.loadRegistry()
            this.loadRows()
        },
        async updatePagination(lazyParams: any) {
            this.updatedRows = []
            this.pagination = {
                start: lazyParams.paginationStart,
                limit: lazyParams.paginationLimit,
                size: lazyParams.size
            }

            if (this.pagination.size > registryDescriptor.paginationLimit) {
                await this.loadRegistry()
                this.loadRows()
            }
        },
        formatPivotRows(row: any) {
            Object.keys(row).forEach((key: any) => {
                if (key !== 'id') {
                    row[key] = row[key].data
                }
            })
        },
        setWarningState(warnings: any[]) {
            this.stopWarningsState = warnings
        }
    }
})
